import Colors from "constants/Colors";
import { Field } from "formik";
import { camelCase, debounce, isObject, uniqueId } from "lodash";
import moment from "moment";
import React, { useContext, useEffect, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import Switch from "react-switch";
import { AuthContext, LoaderContext } from "../../contexts";
import { useGetPersonsSync, useGetSicCode } from "../../services/PersonService";
import { useGetCountries, useGetQuery, useGetSerpro, useGetZipCode } from "../../services/QuestionnairesService";
import { MessageCenter } from "../../utils";
import CheckBoxSimple from "../Form/CheckboxFormSectionSimple";
import CheckBoxGroup from "../Form/CheckBoxGroup";
import ComboBox from "../Form/ComboBox3";
import InputField from "../Form/InputField";
import Label from "../Form/Label";
import MultiSelectField from "../Form/MultiSelectField";
import RadioButtonGroup from "../Form/Radio/RadioButtonGroup";
import SearchField from "../Form/SearchField";
import TextField from "../Form/TextField";
import Icon from "../Icon";
import TextEditorBox from "../TextEditorBox";

import HasPermission from "components/HasPermission/index";
import DragZoneUpload from "../DragZoneUpload/index";
import TagsList from "../TagList/index";
import { AddButton, BorderSwitch, BottomSeparatorLine, CalendarContainer, Container, FieldWrapper } from "./styles";

const getFieldComponent = (question) => {
  const { idDomainQuestion, idQuestion, behaviorObject } = question;
  if (idDomainQuestion === 42) {
    switch (behaviorObject?.componentId) {
      case "TextEditorBox":
        return TextEditorBox;
      case "TextField":
        return TextField;
      default:
        return InputField;
    }
  } else if (idDomainQuestion == 43) {
    switch (behaviorObject?.componentId) {
      case "RadioButton":
        return RadioButtonGroup;
      case "CheckBox":
        return CheckBoxSimple;
      default:
        return ComboBox;
    }
  } else if (idDomainQuestion === 44) {
    switch (behaviorObject?.componentId) {
      case "CheckBox":
        return CheckBoxGroup;
      default:
        return MultiSelectField;
    }
  } else if (idDomainQuestion === 276) {
    return SearchField;
  } else if (idDomainQuestion === 295) {
    return InputField;
  } else if (idDomainQuestion === 277) {
    return InputField;
  }
};

const getQuestionAnswer = (question, setFieldValue) => {
  if (!question) return "";
  if (question.answer == null) question.answer = "";

  //question with options answer
  if (question.idDomainQuestion === 43) {
    if (isNaN(question.behaviorObject.defaultValue)) question.behaviorObject.defaultValue = null;
    if (!question?.idAnswerOption && !question.behaviorObject.defaultValue && question.behaviorObject.componentId === "Switch") {
      const currentAnswerOption = question?.answerOptions?.find((x) => x.dsAnswerOption?.toUpperCase() === "NO");
      if (currentAnswerOption) {
        question.idAnswerOption = currentAnswerOption.idAnswerOption;
        question.behaviorObject.defaultValue = currentAnswerOption.idAnswerOption;
      }
    }

    if (!question?.idAnswerOption && !question.behaviorObject.defaultValue) return;
    if (!question?.idAnswerOption && question.behaviorObject.defaultValue && !isNaN(question.behaviorObject.defaultValue))
      question.idAnswerOption = question.behaviorObject.defaultValue;
    if (!question.idAnswerOption) return;

    let dsAnswerOption;
    let resultValue = null;
    try {
      const currentAnswerOption = question?.answerOptions?.find((x) => x.idAnswerOption === question?.idAnswerOption);
      const { behaviorObject } = currentAnswerOption || {};

      dsAnswerOption = currentAnswerOption?.dsAnswerOption;
      resultValue = behaviorObject?.resultValue != null ? behaviorObject?.resultValue : null;
    } catch (err) {
      console.error(`quotation-edit - checkType: idAnswerOption: ${question.idAnswerOption} - ERROR: ${err.message}`);
    }

    if (!question.answer && dsAnswerOption) question.answer = (resultValue != null ? resultValue : dsAnswerOption) || "";
    setFieldValue &&
      setFieldValue(question.key, {
        idAnswerOption: question?.idAnswerOption,
        dsAnswerOption: dsAnswerOption || "",
        resultValue,
      });
    return;
  } else if (question.idDomainQuestion === 44 && Array.isArray(question.answer)) {
    question.answer = question.answer.filter((v) => question.answerOptions?.find((o) => o.idAnswerOption === v.idAnswerOption));
  }

  //questions with calendar
  if (question.behaviorObject.rightIcon === "calendar") {
    let formattedDate = "";
    if (question.answer) {
      let momentDate = moment(question.answer, question.behaviorObject.fieldDateFormat || "YYYY-MM-DD");
      if (momentDate.isValid()) {
        formattedDate = momentDate.format(question.behaviorObject.fieldDateFormat || "MM/DD/YYYY");
        question.answer = momentDate.format(question.behaviorObject.fieldDateFormat || "YYYY-MM-DD");
      }
    }

    question.answer = question.answer;
    setFieldValue && setFieldValue(question.key, formattedDate);
    return;
  }

  if (question.idDomainQuestion === 270 || question.idDomainQuestion === 271) {
    try {
      let answerJson = JSON.parse(question.answerJson) || [];
      question.answer = answerJson;
    } catch (error) {
      question.answer = [];
      //console.log('ERROR IN PARSE', question);
      return;
    }
  }

  //question with string or json answer
  try {
    let answerJson = JSON.parse(question.answerJson);

    if (answerJson && answerJson.constructor === Object) {
      question.answer = question?.answer || question.behaviorObject.defaultValue || "";
      setFieldValue && setFieldValue(question.key, answerJson?.value || question.behaviorObject.defaultValue || "");
      return;
    } else {
      question.answer = question?.answer || question.behaviorObject.defaultValue || "";
      setFieldValue && setFieldValue(question.key, question?.answer || question.behaviorObject.defaultValue || "");
      return;
    }
  } catch (error) {
    //console.log('ERROR IN PARSE', question);
    question.answer = question?.answer || question.behaviorObject.defaultValue || "";
    setFieldValue && setFieldValue(question.key, question?.answer || question.behaviorObject.defaultValue || "");
    return;
  }
};

const getDisableDays = (days) => {
  if (days == null) {
    return null;
  }
  const date = new Date();
  return new Date(date.setDate(date.getDate() + days));
};

const getOptions = (question, idProduct, idDomainStatusFilter, idDomainOperationFilter) => {
  if (![43, 44, 270, 271].includes(question.idDomainQuestion)) return [];

  if (question.answerOptions) {
    const answerOptions = question.answerOptions.map((answerOption) => {
      let behavior = {};
      let behaviorObject = answerOption.behaviorObject || {};

      if (idDomainStatusFilter) {
        if (
          !Array.isArray(answerOption?.behaviorObject?.idDomainStatusFilter) &&
          answerOption?.behaviorObject?.idDomainStatusFilter !== idDomainStatusFilter
        ) {
          return null;
        } else if (
          Array.isArray(answerOption?.behaviorObject?.idDomainStatusFilter) &&
          !answerOption?.behaviorObject?.idDomainStatusFilter.includes(idDomainStatusFilter)
        ) {
          return null;
        }
      }

      if (idDomainOperationFilter) {
        if (
          !Array.isArray(answerOption?.behaviorObject?.idDomainOperationFilter) &&
          answerOption?.behaviorObject?.idDomainOperationFilter !== idDomainOperationFilter
        ) {
          return null;
        } else if (
          Array.isArray(answerOption?.behaviorObject?.idDomainOperationFilter) &&
          !answerOption?.behaviorObject?.idDomainOperationFilter.includes(idDomainOperationFilter)
        ) {
          return null;
        }
      }

      try {
        behavior = JSON.parse(answerOption.behaviorAnswerOption) || {};
      } catch (e) {
        //console.error('GET OPTIONS - Error in parse Behavior')
      }

      if (behavior?.isResultProduct) {
        const hasProduct = behavior.resultIdProduct.some((resultId) => resultId == idProduct);

        if (hasProduct) {
          return {
            label: answerOption.dsAnswerOption,
            expireDateAnswerOption: answerOption.expireDateAnswerOption,
            value: {
              idAnswerOption: answerOption.idAnswerOption,
              dsAnswerOption: answerOption.dsAnswerOption,
              resultValue: behavior.resultValue != null ? behavior.resultValue : null,
            },
            behaviorObject,
            isReadOnly: behaviorObject?.isReadOnly,
          };
        }
      } else {
        return {
          label: answerOption.dsAnswerOption,
          expireDateAnswerOption: answerOption.expireDateAnswerOption,
          value: {
            idAnswerOption: answerOption.idAnswerOption,
            dsAnswerOption: answerOption.dsAnswerOption,
            resultValue: behavior.resultValue != null ? behavior.resultValue : null,
          },
          behaviorObject,
          isReadOnly: behaviorObject?.isReadOnly,
        };
      }
    });

    return answerOptions.filter(Boolean);
  }

  return [];
};

export default function FormSection({
  sectionName,
  questionnaire,
  values,
  setFieldValue,
  sectionIndex,
  extraButtons,
  onQuestionChange,
  periodicityText,
  periodicityOptions,
  idProduct,
  onClickCreatePerson,
  operationIdPerson,
  disableAll,
  errors,
  setFieldError,
  idDomainStatusFilter,
  idDomainOperationFilter,
  singleUpload,
  onBlur,
  canGenerate,
  question901SearchAllCountry,
  currentModality,
  ignoreModality,
  refreshCompanyClientInformation,
  onRequestSerproCnpj,
  showWarning,
  format,
  customTagListBg,
  onSearchZipCode,
  permissions = {},
  idOperation,
  replaceQuery,
}) {
  const mc = new MessageCenter();
  const authContext = useContext(AuthContext);
  const [filteredQuestionnaire, setFilteredQuestionnaire] = useState([]);
  const [showCalendar, setShowCalendar] = useState("");
  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const isMultipleFormSection = sectionIndex != null;

  const { request: getPersonRequest } = useGetPersonsSync();

  const { request: getSicCodeRequest } = useGetSicCode();

  const { request: getCountryRequest } = useGetCountries();

  const { request: getSerproRequest } = useGetSerpro();

  const { request: getZipCodeRequest } = useGetZipCode();

  const { request: useGetQueryRequest } = useGetQuery();

  const cpfValidation = (cpf) => {
    let sum;
    let rest;
    sum = 0;
    if (cpf == "00000000000") return false;

    for (let i = 1; i <= 9; i++) sum = sum + parseInt(cpf.substring(i - 1, i)) * (11 - i);
    rest = (sum * 10) % 11;

    if (rest == 10 || rest == 11) rest = 0;
    if (rest != parseInt(cpf.substring(9, 10))) return false;

    sum = 0;
    for (let i = 1; i <= 10; i++) sum = sum + parseInt(cpf.substring(i - 1, i)) * (12 - i);
    rest = (sum * 10) % 11;

    if (rest == 10 || rest == 11) rest = 0;
    if (rest != parseInt(cpf.substring(10, 11))) return false;
    return true;
  };

  const cnpjValidation = (cnpj) => {
    let position = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    if (cnpj.length !== 14) return false;

    if (/0{14}/.test(cnpj)) return false;

    for (var i = 0, n = 0; i < 12; n += cnpj[i] * position[++i]);
    if (cnpj[12] != ((n %= 11) < 2 ? 0 : 11 - n)) return false;

    for (var i = 0, n = 0; i <= 12; n += cnpj[i] * position[i++]);
    if (cnpj[13] != ((n %= 11) < 2 ? 0 : 11 - n)) return false;

    return true;
  };

  const handleOnChange = (question, value, filteredQuestionnaire) => {
    if (question.disabled || question.behaviorObject.isReadOnly || question.behaviorObject.rightIcon === "calendar") return;

    setFieldValue && setFieldValue(question.key, value);
    let filteredQuestionnaireUpdated = filteredQuestionnaire;

    if (!question.behaviorObject.searchQueryId || value === "") {
      const idAnswerOption = isObject(value) && value.idAnswerOption ? value.idAnswerOption : undefined;
      const answer = idAnswerOption
        ? value.resultValue != null
          ? value.resultValue
          : value.dsAnswerOption
        : isObject(value)
        ? JSON.stringify(value)
        : value;

      filteredQuestionnaireUpdated = filteredQuestionnaire.map((v) =>
        v.key === question.key
          ? {
              ...v,
              idAnswerOption,
              answer: answer === "" ? (v.answerJson = "") : answer,
              answerJson: idAnswerOption ? undefined : v.answerJson,
            }
          : v
      );

      setFilteredQuestionnaire(filteredQuestionnaireUpdated);
    }
    onQuestionChange && onQuestionChange(question, value, filteredQuestionnaireUpdated, setFilteredQuestionnaire, setFieldValue);
  };

  const debounceOnChange = useRef(
    debounce((question, value, filteredQuestionnaire) => handleOnChange(question, value, filteredQuestionnaire), 50)
  ).current;

  //search request for search field types based on behavior
  const searchRequest = async (searchValue, question, setSearchList) => {
    try {
      let qstBehavior = question.behaviorObject;

      if (replaceQuery) {
        const hasQuery = replaceQuery.find((query) => query.searchQueryId === qstBehavior.searchQueryId);
        if (hasQuery) return setSearchList(hasQuery.values);
      }

      if (qstBehavior && qstBehavior.searchQueryId === "person") {
        const query = { getContacts: true, getSicCode: true };

        if (qstBehavior.searchQueryFieldId === "legalPersonId" && !searchValue) {
          searchValue = "xxxxxxxxxxxxxxxx";
        }

        query[qstBehavior.searchQueryFieldId] = searchValue;

        if (qstBehavior.searchIndividuals) {
          query.idPersonRoot = authContext.idPersonRoot;
          query.idDomain = 34;
        } else if (qstBehavior?.searchGovernment) {
          query.idDomain = 615;
        } else {
          if (qstBehavior.searchOperationIdPerson && operationIdPerson) {
            query.idPersonRoot = operationIdPerson;
          }
          query.idDomain = 35;
        }

        const { data } = await getPersonRequest(null, query);

        const persons = data.value.map((person) => ({
          ...person,
          value: query.idDomain === 34 ? `${person.personFirstName} ${person.personLastName}` : person.personLastName,
          id: person.idPerson,
        }));

        return setSearchList(persons);
      }

      if (qstBehavior && qstBehavior.searchQueryId === "sicCode") {
        const query = { sicType: "TEXEL_MERIDIAN_US", searchValue };

        const { data } = await getSicCodeRequest(null, query);

        const sicCodes = data.value.map((sicCode) => ({
          ...sicCode,
          value: sicCode.sicCode + " - " + sicCode.sicDescription,
          id: sicCode.sicCode,
        }));

        return setSearchList(sicCodes);
      }

      if (qstBehavior && qstBehavior.searchQueryId === "country") {
        const query = { country: searchValue };

        if (!question901SearchAllCountry) {
          if (question.idQuestion == 901) {
            query.countryIds = [242, 221]; //221 USA
          }
        }

        const { data } = await getCountryRequest(null, query);

        const countries = data.value.map((country) => ({
          ...country,
          value: country.countryName,
          id: country.idCountry,
        }));
        return setSearchList(countries);
      }

      if (qstBehavior?.searchQueryId === "fitQueryPersonClient" || qstBehavior?.searchQueryId === "fitQueryPersonClientLegalEntities") {
        const { data } = await useGetQueryRequest({
          id: qstBehavior?.searchQueryId,
          params: {
            id: qstBehavior?.searchQueryId,
            queryTail: qstBehavior?.searchQueryId === "fitQueryPersonClient",
            personName: searchValue,
            legalPersonId: searchValue,
          },
        });

        try {
          if (data.ret_code !== 200) return;

          const persons = data.ret_value.map((person) => ({
            ...person,
            value: qstBehavior?.searchQueryId !== "fitQueryPersonClient" ? `${person.legalPersonId} - ${person.personName}` : person.personName,
            id: person.id,
          }));
          return setSearchList(persons);
        } catch (err) {
          console.log("err cnpj", err);
        }
      }

      if (["fitQueryBorrowerRepresentative", "fitGetSompoInsured"].includes(qstBehavior?.searchQueryId)) {
        const { data } = await useGetQueryRequest({
          id: qstBehavior?.searchQueryId,
          params: {
            id: qstBehavior?.searchQueryId,
            personName: searchValue,
            legalPersonId: searchValue,
            borrowerId: 554875, //554875
          },
        });

        try {
          if (data.ret_code !== 200) return;

          const persons = data.ret_value.map((person) => ({
            ...person,
            value: `${person.name || person.personName}${person.CONTACT_DESCRIPTION ? ` - ${person.CONTACT_DESCRIPTION}` : ""}`,
            id: person.id,
          }));
          return setSearchList(persons);
        } catch (err) {
          console.log("err cnpj", err);
        }
      }

      if (qstBehavior?.searchQueryId === "fitGetCompanyPerson") {
        const { data } = await useGetQueryRequest({
          id: "fitGetCompanyPerson",
          params: {
            id: "fitGetCompanyPerson",
            queryTail: true,
            personName: searchValue,
          },
        });

        try {
          if (data.ret_code !== 200) return;

          const persons = data.ret_value.map((person) => ({
            ...person,
            value: person.personName,
            id: person.id,
          }));
          return setSearchList(persons);
        } catch (err) {
          console.log("err cnpj", err);
        }
      }

      if (qstBehavior?.searchQueryId === "fitGetCountrySubDivisionsByCountryName") {
        const { data } = await useGetQueryRequest({
          id: "fitGetCountrySubDivisionsByCountryName",
          params: {
            id: "fitGetCountrySubDivisionsByCountryName",
            subdivisionName: qstBehavior.countryName?.dsAnswerOption || qstBehavior.countryName,
            name: searchValue,
          },
        });

        try {
          if (data.ret_code !== 200) return;

          const persons = data.ret_value.map((person) => ({
            ...person,
            value: person.SUBDIVISION_NAME,
            id: person.ID_COUNTRY,
          }));
          return setSearchList(persons);
        } catch (err) {
          console.log("err cnpj", err);
        }
      }

      const arrSearchList = ["getSlipLeader", "getLloydsLeader", "getLirmaLeader", "getSecondLloyds", "getSurplusLinesBroker"];
      if (arrSearchList.includes(qstBehavior?.searchQueryId)) {
        const { data } = await useGetQueryRequest({
          id: qstBehavior?.searchQueryId,
          params: {
            id: qstBehavior?.searchQueryId,
            queryTail: true,
            personName: searchValue,
            idOperation,
            isLirmaSearchQuery: qstBehavior?.searchQueryId === "getLirmaLeader",
            selectedLloyds: qstBehavior?.selectedLloyds
          },
        });

        try {
          if (data.ret_code !== 200) return;

          const persons = data.ret_value.map((person) => ({
            ...person,
          }));
          return setSearchList(persons);
        } catch (err) {
          console.log("err cnpj", err);
        }
      }

      if (qstBehavior?.searchQueryId === "fitGetTaxPayerPerson") {
        const { data } = await useGetQueryRequest({
          id: "fitGetTaxPayerPerson",
          params: {
            id: "fitGetTaxPayerPerson",
            queryTail: true,
            personName: searchValue,
            idOperation,
            administeredByAnswer: qstBehavior?.administeredByAnswer || null,
          },
        });

        try {
          if (data.ret_code !== 200) return;

          const persons = data.ret_value.map((person) => ({
            ...person,
            value: person.value,
            id: person.id,
          }));
          return setSearchList(persons);
        } catch (err) {
          console.log("err cnpj", err);
        }
      }

      if (qstBehavior?.searchQueryId) {
        const { data } = await useGetQueryRequest({
          id: qstBehavior?.searchQueryId,
          params: {
            id: qstBehavior?.searchQueryId,
            queryTail: true,
            personName: searchValue,
            idOperation,
          },
        });

        try {
          if (data.ret_code !== 200) return;

          const persons = data.ret_value.map((person) => ({
            ...person,
          }));
          return setSearchList(persons);
        } catch (err) {
          console.log("err cnpj", err);
        }
      }

      return;
    } catch (err) {
      console.log("FETCH_FAILURE: ", err);
      return;
    }
  };

  const searchSerpro = async (question, searchValue, queryId, filteredQuestionnaire, setFieldValue) => {
    validateCpfCnpj(question, searchValue, queryId);
    if (queryId !== "fitGetSerproCpnj" || !searchValue) return;

    const getErrorMessage = (data, question) => {
      let message = "";
      try {
        message =
          data.ret_value.trim() === "Nenhum registro encontrado."
            ? `CPF ou CNPJ não encontrado`
            : data.ret_value.trim() === "CNPJ inválido" || data.ret_value.trim() === "CPF inválido"
            ? `CPF ou CNPJ inválido`
            : `Erro durante a consulta do CPF ou CNPJ`;
      } catch (e) {
        message = `Erro durante a consulta do CPF ou CNPJ`;
      }

      if (message) {
        setFieldError && setFieldError(question.key, message);
        mc.error(message);
      }

      return message;
    };

    try {
      const { data } = await getSerproRequest(null, { id: searchValue });
      if (data.ret_code !== 200) {
        onRequestSerproCnpj && onRequestSerproCnpj(question, {}, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue);
        getErrorMessage(data, question);
        return "";
      }
      onRequestSerproCnpj && onRequestSerproCnpj(question, data.ret_value, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue);
    } catch (err) {
      const data = err.response.data;
      onRequestSerproCnpj && onRequestSerproCnpj(question, {}, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue);
      getErrorMessage(data, question);
    }
  };

  const validateCpfCnpj = async (question, searchValue, queryId) => {
    if (queryId === "fitGetSerproCpnj" || !searchValue) return;

    let message = "";
    if (question.behaviorObject?.fieldMask === "cpf") {
      if (!cpfValidation(searchValue)) message = `CPF ou CNPJ inválido`;
    }

    if (question.behaviorObject?.fieldMask === "cnpj") {
      if (!cnpjValidation(searchValue)) message = `CPF ou CNPJ inválido`;
    }

    if (question.behaviorObject?.fieldMask === "cpfCnpj") {
      if (searchValue && searchValue.length > 11) {
        if (!cnpjValidation(searchValue)) message = `CPF ou CNPJ inválido`;
      } else if (searchValue) {
        if (!cpfValidation(searchValue)) message = `CPF ou CNPJ inválido`;
      }
    }

    if (message) {
      setFieldError && setFieldError(question.key, message);
      mc.error(message);
    }
  };

  const searchZipCode = async (zipCode, queryId, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue) => {
    if (queryId !== "zipCode") return;

    try {
      const { data } = await getZipCodeRequest(null, { zipcodeQuery: zipCode });
      if (data.ret_code !== 200) {
        mc.error(`CEP não encontrado!`);
        console.log("searchZipCode", data);
        return;
      }

      onSearchZipCode && onSearchZipCode(data.ret_value, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue);
    } catch (err) {
      console.log("searchZipCode", err);
    }
  };

  //observer filter questions based on behavior when questionnare is loaded or change
  useEffect(() => {
    const sectionQuestions = questionnaire
      .filter((question) => {
        return question.behaviorObject && question.behaviorObject.sectionName === sectionName;
      })
      .filter((question) => {
        return ignoreModality || !question.productPlans?.length || question.productPlans.includes(currentModality);
      })
      .map((question) => {
        if (!question.key)
          question.key = `${camelCase(question.dsQuestion)}_${question.idQuestion}${isMultipleFormSection ? `_${sectionIndex}` : ""}_${uniqueId()}`;

        if (!ignoreModality) {
          const questionInTheSection = filteredQuestionnaire.find((qst) => qst.key === question.key);
          if (questionInTheSection) return questionInTheSection;
        }

        getQuestionAnswer(question, setFieldValue);
        return question;
      });

    setFilteredQuestionnaire(sectionQuestions);
  }, [questionnaire, currentModality]);

  //observer to send current questionnaire answers externally to other forms
  useEffect(() => {
    // console.log(`$ANSWERS_${sectionName}${sectionIndex != null ? `_${sectionIndex}` : ""}`, filteredQuestionnaire, values)
    setFieldValue(`$ANSWERS_${sectionName}${sectionIndex != null ? `_${sectionIndex}` : ""}`, filteredQuestionnaire);
  }, [filteredQuestionnaire]);

  const handleRefreshPerson = async (question, idPerson) => {
    updateIsLoadingStatus(true);
    const { data, status } = await getPersonRequest(null, {
      getContacts: true,
      getSicCode: true,
      idDomain: 35,
      idPerson,
    });

    if (status !== 200 || !data.value.length) return updateIsLoadingStatus(false);

    const person = data.value[0];

    const filteredQuestionnaireUpdated = filteredQuestionnaire.map((v) =>
      v.key === question.key
        ? {
            ...v,
            answerJson: JSON.stringify({ value: person.personLastName, id: person.idPerson }),
            answer: JSON.stringify({ value: person.personLastName, id: person.idPerson }),
          }
        : v
    );

    setFilteredQuestionnaire(filteredQuestionnaireUpdated);
    onQuestionChange(
      question,
      { value: person.personLastName, id: person.idPerson, ...person },
      filteredQuestionnaireUpdated,
      setFilteredQuestionnaire,
      setFieldValue
    );

    return updateIsLoadingStatus(false);
  };

  const handleActionButtons = (question) => {
    if (refreshCompanyClientInformation && !!question.behaviorObject.canRefreshPerson) {
      return (
        <>
          <AddButton
            type="button"
            style={{ paddingLeft: 8 }}
            onClick={() =>
              onClickCreatePerson && onClickCreatePerson(question, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue, onQuestionChange)
            }
          >
            <Icon name="addEdit" />
          </AddButton>
          <AddButton
            type="button"
            onClick={() => {
              try {
                const { id: idPerson } = JSON.parse(question.answer);
                handleRefreshPerson(question, idPerson);
              } catch (error) {
                return;
              }
            }}
          >
            <Icon name="generate" />
          </AddButton>
        </>
      );
    } else if (!disableAll && !question.disabled && !!question.behaviorObject.canCreatePerson) {
      return (
        <AddButton
          type="button"
          style={{ paddingLeft: 8 }}
          onClick={() =>
            onClickCreatePerson && onClickCreatePerson(question, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue, onQuestionChange)
          }
        >
          <Icon name="addEdit" />
        </AddButton>
      );
    } else {
      return <></>;
    }
  };

  return (
    <Container key={`${sectionName}-${sectionIndex}-section`}>
      {filteredQuestionnaire.map((question, questionIndex) => {
        const fieldComponent = getFieldComponent(question);

        if (!question.visibleQuestion) return <></>;

        const idAnswerOption = question.idAnswerOption;
        const answerOptions = question.answerOptions; //Array de objetos
        let warning = false;

        answerOptions.forEach((option) => {
          const optionValue = option.idAnswerOption;

          if (idAnswerOption === optionValue && option.idDomain == 40) {
            warning = idAnswerOption;
          }
        });

        return (
          <React.Fragment key={`${questionIndex}-${sectionIndex}-${question.idQuestion}-fragment`}>
            {question?.behaviorObject?.lineBreak && <div style={{ width: "100%" }} />}
            <FieldWrapper
              key={`${questionIndex}-${sectionIndex}-${question.idQuestion}-wrapper`}
              flex={
                question.behaviorObject.canCreatePerson ||
                question.behaviorObject.extraDescriptionLeft ||
                question.behaviorObject.displayFlex ||
                question.behaviorObject.componentId === "Switch"
              }
              borderTop={question.behaviorObject.displayFlex}
              mobileWidth={question.behaviorObject.mobileWidth}
              tabletWidth={question.behaviorObject.tabletWidth}
              desktopWidth={question.behaviorObject.desktopWidth}
              marginTop={question.behaviorObject.marginTop}
              marginRight={question.behaviorObject.marginRight}
              paddingRight={question.behaviorObject.paddingRight}
              labelVerticalAlign={question.behaviorObject.labelVerticalAlign}
              >
              {question.behaviorObject.extraDescriptionLeft && (
                <Label hideLabelAsterisk={true} inline={true} width={100} style={{ alignSelf: "center", marginBottom: 20 }} bold>
                  {question.behaviorObject.extraDescriptionLeft}
                </Label>
              )}

              {[270, 271, 614].includes(question.idDomainQuestion) && (
                <Label hideLabelAsterisk={true} inline={true} style={{ alignSelf: "flex-start", marginBottom: 20 }}>
                  {question.dsQuestion}
                </Label>
              )}

              {/* if question is type text, number, search or select */}
              {fieldComponent && question.behaviorObject.componentId !== "Switch" ? (
                <Field
                  row={question.behaviorObject.row}
                  warning={showWarning ? warning : null}
                  key={`${questionIndex}-${sectionIndex}-${question.idQuestion}-field`}
                  extraLabelTop={question.behaviorObject.extraDescriptionTop}
                  disabled={disableAll || question.disabled || (question.behaviorObject.isReadOnly)}
                  readOnly={question.behaviorObject.isReadOnly || question.behaviorObject.rightIcon === "calendar"}
                  mask={question.fieldMask}
                  inline={question.behaviorObject.displayFlex}
                  isRequired={question.behaviorObject.isRequired}
                  hideLabelAsterisk={question.behaviorObject.hideDescription}
                  name={question.key}
                  fieldName={question.key}
                  label={question.behaviorObject.hideDescription ? "" : `${question.dsQuestion}`}
                  additionalInfo={question?.behaviorObject?.additionalInfo ? question.behaviorObject.additionalInfo : ""}
                  //label={question.behaviorObject.hideDescription ? '' : `${question.dsQuestion}`}
                  placeholder={question.behaviorObject.placeholder || " "}
                  validation={true}
                  type="text"
                  onBlur={(value) => {
                    setTimeout(() => {
                      onBlur && onBlur(question, value, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue);
                      searchZipCode(
                        values && values[question.key],
                        question.behaviorObject.searchQueryId,
                        filteredQuestionnaire,
                        setFilteredQuestionnaire,
                        setFieldValue
                      );
                      searchSerpro(
                        question,
                        values && values[question.key],
                        question.behaviorObject.searchQueryId,
                        filteredQuestionnaire,
                        setFieldValue
                      );
                    }, 350);
                  }}
                  leftIcon={question.behaviorObject.leftIcon}
                  leftIconText={question.behaviorObject.leftIconText}
                  rightIcon={question.behaviorObject.rightIcon}
                  rightIconClick={() => {
                    if (disableAll || question.disabled || question.behaviorObject.isReadOnly) return;
                    if (question.behaviorObject.rightIcon === "calendar") return setShowCalendar(!showCalendar.trim().length ? question.key : "");
                    setTimeout(() => {
                      searchSerpro(
                        question,
                        values && values[question.key],
                        question.behaviorObject.searchQueryId,
                        filteredQuestionnaire,
                        setFieldValue
                      );
                      searchZipCode(
                        values && values[question.key],
                        question.behaviorObject.searchQueryId,
                        filteredQuestionnaire,
                        setFilteredQuestionnaire,
                        setFieldValue
                      );
                    }, 350);
                  }}
                  format={(value) => {
                    try {
                      if (question.idDomainQuestion !== 44) return;
                      return value?.map((v) => v.dsAnswerOption)?.join(", ") || "";
                    } catch (e) {}
                  }}
                  component={fieldComponent}
                  options={getOptions(question, idProduct, idDomainStatusFilter, idDomainOperationFilter, permissions) || []}
                  value={values && values[question.key]}
                  onChange={(value) => {
                    if (question.disabled || question.behaviorObject.isReadOnly || (question.behaviorObject.rightIcon === "calendar" && value)) return;

                    setFieldValue && setFieldValue(question.key, value);
                    let filteredQuestionnaireUpdated = filteredQuestionnaire;

                    if (!question.behaviorObject.searchQueryId || value === "") {
                      const idAnswerOption = isObject(value) && value.idAnswerOption ? value.idAnswerOption : undefined;
                      const answer = idAnswerOption
                        ? value.resultValue != null
                          ? value.resultValue
                          : value.dsAnswerOption
                        : isObject(value)
                        ? JSON.stringify(value)
                        : value;

                      filteredQuestionnaireUpdated = filteredQuestionnaire.map((v) =>
                        v.key === question.key
                          ? {
                              ...v,
                              idAnswerOption,
                              answer: answer === "" ? (v.answerJson = "") : answer,
                              answerJson: idAnswerOption ? undefined : v.answerJson,
                            }
                          : v
                      );

                      setFilteredQuestionnaire(filteredQuestionnaireUpdated);
                    }
                    onQuestionChange && onQuestionChange(question, value, filteredQuestionnaireUpdated, setFilteredQuestionnaire, setFieldValue);
                  }}
                  searchRequest={searchRequest}
                  question={question}
                  canGenerate={question.behaviorObject.canGenerate || canGenerate}
                  onSearchSelect={(selectedValue) => {
                    if (selectedValue) {
                      const filteredQuestionnaireUpdated = filteredQuestionnaire.map((v) =>
                        v.key === question.key
                          ? {
                              ...v,
                              answerJson: JSON.stringify({ value: selectedValue.value, id: selectedValue.id }),
                              answer: JSON.stringify({ value: selectedValue.value, id: selectedValue.id }),
                            }
                          : v
                      );

                      setFilteredQuestionnaire(filteredQuestionnaireUpdated);
                      onQuestionChange &&
                        onQuestionChange(question, selectedValue, filteredQuestionnaireUpdated, setFilteredQuestionnaire, setFieldValue);
                    }
                  }}
                  labelVerticalAlign={question.behaviorObject.labelVerticalAlign}
                  onlyShowOptionsLabel={question.behaviorObject.onlyShowOptionsLabel}
                />
              ) : (
                <></>
              )}

              {showCalendar && showCalendar === question.key ? (
                <CalendarContainer isFlex={question.behaviorObject.displayFlex}>
                  <DayPicker
                    mode="single"
                    fromYear={1900}
                    toYear={2100}
                    captionLayout="dropdown-buttons"
                    style
                    firstDayOfWeek={0}
                    disabled={[
                      {
                        before: getDisableDays(question.behaviorObject?.fieldMinValue) || new Date(),
                      },
                      {
                        after: getDisableDays(question.behaviorObject?.fieldMaxValue),
                      },
                    ]}
                    fixedWeeks
                    onDayClick={(date, { disabled }) => {
                      if (disabled) return;
                      let formattedDate = "";
                      let selectedDate = "";
                      let momentDate = moment(new Date(date));
                      if (momentDate.isValid()) {
                        formattedDate = momentDate.format(question.behaviorObject.fieldDateFormat || "MM/DD/YYYY");
                        selectedDate = momentDate.format(question.behaviorObject.fieldDateFormat || "YYYY-MM-DD");
                      }

                      setFieldValue && setFieldValue(question.key, formattedDate);
                      let filteredQuestionnaireUpdated = filteredQuestionnaire.map((v) =>
                        v.key === question.key ? { ...v, answer: selectedDate } : v
                      );
                      setFilteredQuestionnaire(filteredQuestionnaireUpdated);
                      setShowCalendar("");

                      //optimization
                      onQuestionChange &&
                        onQuestionChange(question, selectedDate, filteredQuestionnaireUpdated, setFilteredQuestionnaire, setFieldValue);
                      // onQuestionChange && onQuestionChange(question, formattedDate, selectedDate, filteredQuestionnaireUpdated, setFilteredQuestionnaire, setFieldValue);
                    }}
                  ></DayPicker>
                </CalendarContainer>
              ) : (
                <></>
              )}

              {/* if question is type switch (yes or no) */}
              {question.behaviorObject.componentId === "Switch" ? (
                <>
                  <Label
                    width={question.behaviorObject.width || 35}
                    isRequired={question.behaviorObject.isRequired}
                    inline={true}
                    hideLabelAsterisk={!question.behaviorObject.isRequired}
                    color={errors && errors[question.key] ? Colors.DANGER : false}
                    error={errors && errors[question.key]}
                  >
                    {`${question.dsQuestion}\n`}
                  </Label>
                  <BorderSwitch error={errors && errors[question.key]}>
                    <Switch
                      disabled={disableAll || question.disabled}
                      height={27}
                      width={53}
                      checked={
                        values &&
                        values[question.key] &&
                        (values[question.key] === "YES" ||
                          values[question.key].dsAnswerOption === "YES" ||
                          values[question.key] === "Yes" ||
                          values[question.key].dsAnswerOption === "Yes" ||
                          values[question.key] == "SIM" ||
                          (values[question.key] == "Sim") | (values[question.key].dsAnswerOption === "SIM") ||
                          values[question.key].dsAnswerOption === "Sim")
                      }
                      onChange={(value) => {
                        setFieldError && setFieldError(question.key, "");
                        const answerOption = value
                          ? question.answerOptions.find((v) => v.dsAnswerOption.toUpperCase() === "YES" || v.dsAnswerOption.toUpperCase() == "SIM")
                          : question.answerOptions.find((v) => v.dsAnswerOption.toUpperCase() === "NO" || v.dsAnswerOption.toUpperCase() == "NÃO");

                        const filteredQuestionnaireUpdated = filteredQuestionnaire.map((v) =>
                          v.key === question.key
                            ? {
                                ...v,
                                answer: answerOption.dsAnswerOption,
                                idAnswerOption: answerOption.idAnswerOption,
                                answerJson: undefined,
                              }
                            : v
                        );
                        setFilteredQuestionnaire(filteredQuestionnaireUpdated);
                        setFieldValue && setFieldValue(question.key, answerOption.dsAnswerOption);
                        onQuestionChange &&
                          onQuestionChange(question, answerOption, filteredQuestionnaireUpdated, setFilteredQuestionnaire, setFieldValue);
                      }}
                      name={question.key}
                      handleDiameter={20}
                      checkedIcon={false}
                      uncheckedIcon={false}
                      offColor={Colors.WHITE}
                      onColor={Colors.GRAY[6] || Colors.GREEN}
                      offHandleColor={errors && errors[question.key] ? Colors.DANGER : Colors.GRAY[6]}
                      onHandleColor={Colors.WHITE}
                    />
                  </BorderSwitch>
                </>
              ) : (
                <></>
              )}

              {handleActionButtons(question)}
            </FieldWrapper>

            {(question.idDomainQuestion === 270 || question.idDomainQuestion === 271) && (
              <>
                <HasPermission permission={!singleUpload || (singleUpload && !question.answer.length)}>
                  <DragZoneUpload
                    isDraft={true}
                    multiple={!singleUpload}
                    idOperation={idOperation}
                    question={question}
                    autoUpload={question.autoUpload}
                    disabled={disableAll || question.disabled}
                    documentTypeRequired={true}
                    documentType={values.documentType || question.documentType}
                    format={question.behaviorObject?.acceptedFileFormat}
                    options={getOptions(question, idProduct, idDomainStatusFilter, idDomainOperationFilter)}
                    periodicityOptions={periodicityOptions}
                    periodicityText={periodicityText}
                    translateText={ values.translateText ? true : false }
                    childrenOptions={
                      question.childrenQuestions ? getOptions(question.childrenQuestions[0].idQuestion, idProduct, idDomainStatusFilter, idDomainOperationFilter) : null
                    }
                    onSuccess={(uploadedFiles) => {
                      const filteredQuestionnaireUpdated = filteredQuestionnaire.map((v) => {
                        if (v.key !== question.key) return v;

                        return {
                          ...v,
                          answer: singleUpload ? uploadedFiles : [...question.answer, ...uploadedFiles],
                          answerJson: singleUpload ? uploadedFiles : [...question.answer, ...uploadedFiles],
                        };
                      });

                      setFilteredQuestionnaire(filteredQuestionnaireUpdated);

                      if (setFieldValue) {
                        setFieldValue(question.key, singleUpload ? uploadedFiles : [...question.answer, ...uploadedFiles]);
                      }

                      if (onQuestionChange) {
                        onQuestionChange(question, uploadedFiles, filteredQuestionnaireUpdated, setFilteredQuestionnaire, setFieldValue);
                      }
                    }}
                  />
                </HasPermission>

                <FieldWrapper mobileWidth={100} tabletWidth={100} desktopWidth={100}>
                  <TagsList
                    disabled={disableAll || question.disabled}
                    tags={question.answer || []}
                    customColor={customTagListBg}
                    removeTag={(currentAttachment) => {
                      const filteredQuestionnaireUpdated = filteredQuestionnaire.map((v) =>
                        v.key === question.key
                          ? {
                              ...v,
                              answer: question.answer.map((attachment) =>
                                question.answer.indexOf(attachment) === currentAttachment ? { ...attachment, deleteAttachment: true } : attachment
                              ),
                              answerJson: question.answer.map((attachment) =>
                                question.answer.indexOf(attachment) === currentAttachment ? { ...attachment, deleteAttachment: true } : attachment
                              ),
                            }
                          : v
                      );
                      setFilteredQuestionnaire(filteredQuestionnaireUpdated);
                      setFieldValue &&
                        setFieldValue(
                          question.key,
                          question.answer.map((attachment) =>
                            question.answer.indexOf(attachment) === currentAttachment ? { ...attachment, deleteAttachment: true } : attachment
                          )
                        );
                      onQuestionChange &&
                        onQuestionChange(question, currentAttachment, filteredQuestionnaireUpdated, setFilteredQuestionnaire, setFieldValue);
                    }}
                  />
                </FieldWrapper>
              </>
            )}

            {question.behaviorObject?.bottomSeparatorLine && <BottomSeparatorLine />}
          </React.Fragment>
        );
      })}
      {!disableAll && extraButtons && extraButtons()}
    </Container>
  );
}
